// Карта доступа: functionId → требуемый тариф
// Синхронизировано с backend AccessControlService.FEATURE_TIER_MAP
export interface FeatureAccess {
  [functionId: string]: {
    name: string;
    requiredTier: 'free' | 'base' | 'premium' | 'pro';
    description?: string;
  };
}

export const FEATURE_ACCESS_MAP: FeatureAccess = {
  // Free
  'daily': { name: 'Ежедневный прогноз', requiredTier: 'free' },
  'horoscope_basic': { name: 'Базовый гороскоп', requiredTier: 'free' },
  'life_path': { name: 'Число жизненного пути', requiredTier: 'free' },
  'profile_limited': { name: 'Профиль личности (базовый)', requiredTier: 'free' },
  'articles': { name: 'Статьи', requiredTier: 'free' },

  // Base
  'weekly': { name: 'Еженедельный прогноз', requiredTier: 'base' },
  'monthly': { name: 'Ежемесячный прогноз', requiredTier: 'base' },
  'love': { name: 'Прогноз любви', requiredTier: 'base' },
  'family': { name: 'Семейный прогноз', requiredTier: 'base' },
  'health': { name: 'Прогноз здоровья', requiredTier: 'base' },
  'transits': { name: 'Транзиты', requiredTier: 'base' },
  'progressions': { name: 'Прогрессии', requiredTier: 'base' },
  'solar': { name: 'Соляр', requiredTier: 'base' },
  'lunar': { name: 'Лунар', requiredTier: 'base' },
  'lunar-returns': { name: 'Лунные возвраты', requiredTier: 'base' },
  'eclipses': { name: 'Затмения', requiredTier: 'base' },
  'numerology-year': { name: 'Персональный год', requiredTier: 'base' },
  'numerology-month': { name: 'Персональный месяц', requiredTier: 'base' },
  'numerology-day': { name: 'Персональный день', requiredTier: 'base' },
  'lucky-dates': { name: 'Счастливые даты', requiredTier: 'base' },
  'synastry': { name: 'Синастрия', requiredTier: 'base' },
  'karmic-compat': { name: 'Кармическая совместимость', requiredTier: 'base' },
  'professional-compat': { name: 'Профессиональная совместимость', requiredTier: 'base' },
  'friendship': { name: 'Дружеская совместимость', requiredTier: 'base' },
  'natal-chart': { name: 'Натальная карта', requiredTier: 'free' },
  'numerology-full': { name: 'Полная нумерология', requiredTier: 'base' },
  'horoscope_personal': { name: 'Персональный гороскоп', requiredTier: 'base' },
  'numerology_basic': { name: 'Базовая нумерология', requiredTier: 'base' },
  'compatibility_basic': { name: 'Базовая совместимость', requiredTier: 'base' },

  // Premium
  'yearly': { name: 'Годовой прогноз', requiredTier: 'premium' },
  'business': { name: 'Бизнес прогноз', requiredTier: 'premium' },
  'karmic': { name: 'Кармические узлы', requiredTier: 'premium' },
  'past-lives': { name: 'Прошлые жизни', requiredTier: 'premium' },
  'destiny-path': { name: 'Путь предназначения', requiredTier: 'premium' },
  'electional': { name: 'Выбор даты (элективная)', requiredTier: 'premium' },
  'sexual': { name: 'Сексуальная совместимость', requiredTier: 'premium' },
  'email-newsletters': { name: 'Email-рассылки', requiredTier: 'premium' },
  'pdf_export': { name: 'PDF экспорт', requiredTier: 'pro' },
  'forecast_monthly': { name: 'Месячный прогноз', requiredTier: 'premium' },

  // Pro
  'custom': { name: 'Кастомный вопрос', requiredTier: 'pro' },
  'business-compat': { name: 'Бизнес совместимость', requiredTier: 'pro' },
  'transits-advanced': { name: 'Расширенные транзиты', requiredTier: 'pro' },
  'child-profiles': { name: 'Детские профили', requiredTier: 'pro' },
  'telegram-newsletters': { name: 'Telegram-рассылки', requiredTier: 'pro' },
  'consult_discount': { name: 'Скидка на консультации', requiredTier: 'pro' },
  'forecast_year': { name: 'Годовой прогноз', requiredTier: 'pro' },
};

// Карта route → functionId
export const ROUTE_TO_FEATURE: Record<string, string> = {
  '/horoscope': 'horoscope_basic',
  '/numerology': 'numerology_basic',
  '/dashboard': 'profile_limited',
  '/natal': 'natal-chart',
  '/compatibility': 'compatibility_basic',
  '/compatibility/synastry': 'synastry',
  '/transits': 'transits',
  '/forecasts': 'monthly',
  '/forecasts/child': 'child-profiles',
  '/electional': 'electional',
  '/forecasts/medical': 'health',
};

// Порядок тарифов (для сравнения)
export const TIER_ORDER = {
  free: 0,
  base: 1,
  premium: 2,
  pro: 3,
};

// Проверка: имеет ли пользователь доступ к функции
export function hasFeatureAccess(
  userTier: string | undefined,
  featureId: string
): boolean {
  const feature = FEATURE_ACCESS_MAP[featureId];
  if (!feature) return true; // Нет в карте = доступно

  const userLevel = TIER_ORDER[userTier as keyof typeof TIER_ORDER] ?? 0;
  const requiredLevel = TIER_ORDER[feature.requiredTier];

  return userLevel >= requiredLevel;
}

// Получить требуемый тариф для route
export function getRequiredTierForRoute(route: string): string | null {
  const featureId = ROUTE_TO_FEATURE[route];
  if (!featureId) return null;
  return FEATURE_ACCESS_MAP[featureId]?.requiredTier || null;
}
